import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import ConfettiBurst from "@/components/feature/ConfettiBurst";
import {
  clinics,
  amputationSpecialtyTags,
  hipaaConsentText,
  postConsentConfirmation,
  globalComplianceText,
  mockPatient,
} from "@/mocks/demo-flow";

type FilterKey =
  | "below-knee"
  | "above-knee"
  | "bilateral-bk"
  | "upper-extremity"
  | "other"
  | "all";

const amputationLabels: Record<string, string> = {
  all: "All Amputation Types",
  "below-knee": "Below Knee (Transtibial)",
  "above-knee": "Above Knee (Transfemoral)",
  "bilateral-bk": "Bilateral Below Knee (BBKA)",
  "upper-extremity": "Upper Extremity",
  other: "Other",
};

const distanceOptions = [
  { value: "all", label: "Any Distance" },
  { value: "5", label: "Within 5 miles" },
  { value: "10", label: "Within 10 miles" },
  { value: "25", label: "Within 25 miles" },
];

const ratingOptions = [
  { value: "all", label: "Any Rating" },
  { value: "4.5", label: "4.5 & up" },
  { value: "4.0", label: "4.0 & up" },
  { value: "3.5", label: "3.5 & up" },
];

export default function ClinicSearchPage() {
  const [selectedClinic, setSelectedClinic] = useState<string | null>(null);
  const [showConsent, setShowConsent] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [agreedClinic, setAgreedClinic] = useState("");

  const [filterAmputation, setFilterAmputation] = useState<FilterKey>("all");
  const [filterDistance, setFilterDistance] = useState("all");
  const [filterRating, setFilterRating] = useState("all");
  const [filterOpen, setFilterOpen] = useState<
    "amputation" | "distance" | "rating" | null
  >(null);

  const zipCode = sessionStorage.getItem("intake_zip") || mockPatient.zipCode;
  const amputationType =
    sessionStorage.getItem("intake_amputation_type") || "below-knee";
  const specialtyTag =
    amputationSpecialtyTags[amputationType] || amputationSpecialtyTags["other"];
  const insuranceProvider =
    sessionStorage.getItem("intake_insurance") || mockPatient.insuranceProvider;

  const filteredClinics = useMemo(() => {
    let result = clinics;

    if (filterAmputation !== "all") {
      const specMap: Record<string, string> = {
        "below-knee": "Below Knee",
        "above-knee": "Above Knee",
        "bilateral-bk": "Bilateral Below Knee",
        "upper-extremity": "Upper Extremity",
      };
      const targetSpec = specMap[filterAmputation];
      if (targetSpec) {
        result = result.filter((c) => c.specialties.includes(targetSpec));
      }
    }

    if (filterDistance !== "all") {
      const maxDist = parseFloat(filterDistance);
      result = result.filter((c) => {
        const dist = parseFloat(c.distance);
        return dist <= maxDist;
      });
    }

    if (filterRating !== "all") {
      const minRating = parseFloat(filterRating);
      result = result.filter((c) => c.rating >= minRating);
    }

    return result;
  }, [filterAmputation, filterDistance, filterRating]);

  useEffect(() => {
    if (consentGiven) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [consentGiven]);

  const handleSelectClinic = (clinicId: string) => {
    setSelectedClinic(clinicId);
  };

  const handleRequestContact = (clinicId: string) => {
    const clinic = clinics.find((c) => c.id === clinicId);
    if (clinic) {
      setAgreedClinic(clinic.name);
      setShowConsent(true);
    }
  };

  const handleAgree = () => {
    setShowConsent(false);
    setConsentGiven(true);

    try {
      const params = new URLSearchParams();
      params.append("clinic_name", agreedClinic);
      params.append("patient_zip", zipCode);
      params.append("amputation_type", amputationType);
      params.append("insurance_provider", insuranceProvider);
      params.append("contact_method", mockPatient.contactMethod);

      fetch("https://readdy.ai/api/form/d8hk3eliodfui947u1qg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      }).catch(() => {});
    } catch {
      // silently handle
    }
  };

  const handleDecline = () => {
    setShowConsent(false);
    setSelectedClinic(null);
    setAgreedClinic("");
  };

  const toggleFilter = (name: "amputation" | "distance" | "rating") => {
    setFilterOpen(filterOpen === name ? null : name);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filterAmputation !== "all") count += 1;
    if (filterDistance !== "all") count += 1;
    if (filterRating !== "all") count += 1;
    return count;
  };

  if (consentGiven) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        {/* Confetti Celebration */}
        <ConfettiBurst />

        {/* Header */}
        <header className="h-16 lg:h-20 flex items-center px-4 md:px-6 lg:px-8 border-b border-background-100 bg-white flex-shrink-0">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="w-7 h-7 lg:w-8 lg:h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-heart-pulse-line text-base"></i>
            </span>
            <span className="font-heading text-sm lg:text-base font-bold text-foreground-950 whitespace-nowrap">
              Quick Prosthetics
            </span>
          </Link>
        </header>

        {/* Post-Consent Confirmation — CELEBRATORY */}
        <main className="flex-1 flex items-center justify-center px-4 md:px-6 py-8 md:py-14 relative overflow-hidden">
          {/* Animated warm background glow */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full bg-accent-100/30 blur-[100px] animate-pulse" />
            <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-primary-100/25 blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-accent-50/30 blur-3xl"></div>
            {/* Floating celebration dots */}
            <div
              className="absolute top-1/4 left-[15%] w-2 h-2 rounded-full bg-accent-400/40 animate-bounce"
              style={{ animationDuration: "3s" }}
            />
            <div
              className="absolute top-1/3 right-[20%] w-1.5 h-1.5 rounded-full bg-primary-400/50 animate-bounce"
              style={{ animationDuration: "2.5s", animationDelay: "0.5s" }}
            />
            <div
              className="absolute top-[40%] left-[25%] w-2.5 h-2.5 rounded-full bg-accent-300/40 animate-bounce"
              style={{ animationDuration: "3.5s", animationDelay: "1s" }}
            />
            <div
              className="absolute top-[55%] right-[15%] w-1.5 h-1.5 rounded-full bg-primary-300/50 animate-bounce"
              style={{ animationDuration: "2.8s", animationDelay: "0.3s" }}
            />
            <div
              className="absolute bottom-[30%] left-[10%] w-2 h-2 rounded-full bg-accent-400/35 animate-bounce"
              style={{ animationDuration: "3.2s", animationDelay: "1.5s" }}
            />
          </div>

          <div className="relative z-10 w-full max-w-[700px] mx-auto">
            {/* ===== CELEBRATION CARD ===== */}
            <div className="bg-white rounded-2xl border border-primary-200 p-8 md:p-12 text-center animate-fadeSlideIn relative overflow-hidden">
              {/* Subtle card glow accent */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] h-[120px] bg-accent-100/40 blur-[60px] rounded-full pointer-events-none" />

              {/* BIG CELEBRATORY ICON */}
              <div className="relative z-10 w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-accent-100 to-accent-50 flex items-center justify-center ring-4 ring-accent-100/40">
                <span className="w-12 h-12 flex items-center justify-center text-primary-600">
                  <i className="ri-check-double-line text-4xl"></i>
                </span>
              </div>

              {/* CELEBRATORY HEADLINE */}
              <h1 className="relative z-10 font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-3 leading-tight">
                You did it!
              </h1>
              <p className="relative z-10 text-lg md:text-xl font-semibold text-primary-600 mb-2">
                We're so excited for you.
              </p>
              <p className="relative z-10 text-sm md:text-base text-foreground-500 leading-relaxed mb-8 max-w-lg mx-auto">
                Everything you shared is now securely with{" "}
                <strong className="text-foreground-700">{agreedClinic}</strong>.
                A real human — a certified prosthetist who specializes in cases
                like yours — will personally reach out within 24 hours. You're
                so close to walking comfortably again.
              </p>

              {/* ===== SUMMARY CARD — warmer style ===== */}
              <div className="relative z-10 rounded-2xl border border-background-200 bg-background-50 p-6 md:p-7 text-left space-y-4 mb-8">
                <div className="flex items-center gap-3 pb-4 border-b border-background-100">
                  <span className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-4-line text-lg text-primary-600"></i>
                  </span>
                  <div>
                    <p className="text-sm md:text-base font-bold text-foreground-900">
                      {agreedClinic}
                    </p>
                    <p className="text-[11px] text-foreground-400">
                      Your chosen clinic — they've been notified
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-[12px] md:text-sm">
                  <div className="flex items-start gap-2.5">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 mt-0.5">
                      <i className="ri-map-pin-2-line text-xs"></i>
                    </span>
                    <div>
                      <span className="text-foreground-400 text-[10px] md:text-[11px] block">
                        ZIP Code
                      </span>
                      <p className="font-semibold text-foreground-700">
                        {zipCode}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 mt-0.5">
                      <i className="ri-body-scan-line text-xs"></i>
                    </span>
                    <div>
                      <span className="text-foreground-400 text-[10px] md:text-[11px] block">
                        Amputation Type
                      </span>
                      <p className="font-semibold text-foreground-700">
                        {amputationLabels[amputationType]}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 mt-0.5">
                      <i className="ri-bank-card-line text-xs"></i>
                    </span>
                    <div>
                      <span className="text-foreground-400 text-[10px] md:text-[11px] block">
                        Insurance
                      </span>
                      <p className="font-semibold text-foreground-700">
                        {insuranceProvider}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 mt-0.5">
                      <i className="ri-smartphone-line text-xs"></i>
                    </span>
                    <div>
                      <span className="text-foreground-400 text-[10px] md:text-[11px] block">
                        Preferred Contact
                      </span>
                      <p className="font-semibold text-foreground-700">
                        {mockPatient.contactMethod}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ===== WHAT HAPPENS NEXT — timeline ===== */}
              <div className="relative z-10 mb-8">
                <p className="text-xs md:text-sm font-semibold text-foreground-700 mb-5">
                  Here's what happens next:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="relative flex flex-col items-center text-center">
                    <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mb-3">
                      <span className="w-6 h-6 flex items-center justify-center text-accent-600">
                        <i className="ri-mail-send-line text-lg"></i>
                      </span>
                    </div>
                    <p className="text-xs font-bold text-foreground-800 mb-1">
                      Profile Transferred
                    </p>
                    <p className="text-[10px] md:text-[11px] text-foreground-400 leading-relaxed">
                      Your intake data, scan metrics, and K-Level assessment are
                      securely shared.
                    </p>
                    <span className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-50 text-[10px] font-semibold text-accent-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" />
                      Done
                    </span>
                  </div>

                  <div className="relative flex flex-col items-center text-center">
                    <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-3">
                      <span className="w-6 h-6 flex items-center justify-center text-primary-600">
                        <i className="ri-user-heart-line text-lg"></i>
                      </span>
                    </div>
                    <p className="text-xs font-bold text-foreground-800 mb-1">
                      Clinic Reviews Your Case
                    </p>
                    <p className="text-[10px] md:text-[11px] text-foreground-400 leading-relaxed">
                      A prosthetist reviews your information and prepares for
                      your first consultation.
                    </p>
                    <span className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-50 text-[10px] font-semibold text-primary-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />
                      In progress
                    </span>
                  </div>

                  <div className="relative flex flex-col items-center text-center">
                    <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mb-3">
                      <span className="w-6 h-6 flex items-center justify-center text-accent-600">
                        <i className="ri-phone-line text-lg"></i>
                      </span>
                    </div>
                    <p className="text-xs font-bold text-foreground-800 mb-1">
                      They Contact You
                    </p>
                    <p className="text-[10px] md:text-[11px] text-foreground-400 leading-relaxed">
                      Within 24 hours, the clinic reaches out to schedule your
                      appointment.
                    </p>
                    <span className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-50 text-[10px] font-semibold text-accent-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-400" />
                      Coming up
                    </span>
                  </div>
                </div>
              </div>

              {/* Post-script encouragement */}
              <p className="relative z-10 text-[11px] md:text-xs text-foreground-400 mt-6 leading-relaxed">
                {postConsentConfirmation}
              </p>
            </div>
          </div>
        </main>

        {/* Compliance Footer — warm */}
        <footer className="w-full bg-background-100 border-t border-background-200 mt-auto">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
            <div className="flex items-start gap-2.5">
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-400 mt-0.5">
                <i className="ri-information-line text-[11px]"></i>
              </span>
              <p className="text-[10px] md:text-[11px] text-foreground-500 leading-relaxed">
                {globalComplianceText}
              </p>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      {/* Header */}
      <header className="h-16 lg:h-20 flex items-center px-4 md:px-6 lg:px-8 border-b border-background-100 bg-white flex-shrink-0">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <span className="w-7 h-7 lg:w-8 lg:h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-heart-pulse-line text-base"></i>
          </span>
          <span className="font-heading text-sm lg:text-base font-bold text-foreground-950 whitespace-nowrap">
            Quick Prosthetics
          </span>
        </Link>
        <div className="ml-auto flex items-center gap-4">
          <Link
            to="/telehealth-consultation"
            className="text-[11px] md:text-xs font-semibold text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            ← Back to Telehealth
          </Link>
          <Link
            to="/ai-artifacts"
            className="text-[11px] md:text-xs font-semibold text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap border border-accent-200 rounded-full px-3 py-1 hover:bg-accent-50"
          >
            Clinical Portal
          </Link>
        </div>
      </header>

      {/* ===== WARM HERO SECTION ===== */}
      <section className="relative overflow-hidden">
        {/* Hero background image */}
        <div className="relative h-[320px] md:h-[400px] w-full overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20cozy%20home%20interior%20with%20morning%20sunlight%20streaming%20through%20a%20large%20window%2C%20a%20wooden%20table%20with%20an%20open%20laptop%20and%20a%20steaming%20ceramic%20mug%20of%20tea%2C%20a%20soft%20throw%20blanket%20draped%20over%20a%20nearby%20chair%2C%20potted%20plant%20with%20gentle%20green%20leaves%2C%20calm%20and%20hopeful%20atmosphere%2C%20cream%20beige%20and%20soft%20terracotta%20tones%2C%20hygge%20inspired%20interior%20photography%2C%20soft%20diffused%20natural%20light%2C%20lifestyle%20editorial%20style%2C%20feeling%20of%20possibility%20and%20comfort&width=1400&height=500&seq=clinic-search-hero-warm&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/45" />

          {/* Hero content overlay */}
          <div className="absolute inset-0 flex items-center justify-center px-4 md:px-6">
            <div className="text-center max-w-[640px]">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/20 text-[10px] md:text-[11px] font-semibold text-background-50/90 mb-4">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-heart-line text-[9px]"></i>
                </span>
                You're almost there
              </span>
              <h1 className="font-heading text-xl md:text-3xl lg:text-4xl font-bold text-background-50 leading-tight mb-3">
                You've done the hard part.
                <br />
                <span className="italic font-serif text-accent-300">
                  Now let's find the right team
                </span>{" "}
                to get you walking comfortably again.
              </h1>
              <p className="text-sm md:text-base text-background-50/80 max-w-lg mx-auto leading-relaxed">
                Based on everything you've shared with us — your location,
                amputation type, insurance — here are clinics near you ready to
                welcome you. Take your time. This is a big decision, and we want
                you to feel completely comfortable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 md:px-6 py-8 md:py-10 space-y-6 relative">
        {/* Warm Background Blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 right-0 w-[500px] h-[500px] rounded-full bg-accent-100/25 blur-3xl"></div>
          <div className="absolute top-60 -left-40 w-[400px] h-[400px] rounded-full bg-primary-100/15 blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-[350px] h-[350px] rounded-full bg-accent-50/20 blur-3xl"></div>
        </div>

        {/* Intro text — warm reassurance */}
        <div className="relative z-10 text-center max-w-[600px] mx-auto pb-2">
          <p className="text-sm text-foreground-500 leading-relaxed">
            Every one of these clinics has certified prosthetists who specialize
            in cases like yours. We've already verified they accept your
            insurance and have availability. All you need to do is choose the
            one that feels right.
          </p>
        </div>

        {/* Filter Bar — softer, warmer */}
        <div className="relative z-20 flex flex-wrap items-center gap-3 p-4 rounded-2xl bg-white border border-background-200/80">
          {/* Location Pill */}
          <div className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-background-50 text-[11px] md:text-xs font-semibold text-foreground-600">
            <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
              <i className="ri-map-pin-2-line text-xs"></i>
            </span>
            Near {zipCode}
          </div>

          {/* Active filter from intake */}
          <div className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-accent-50 text-[11px] md:text-xs font-semibold text-accent-700">
            <span className="w-4 h-4 flex items-center justify-center text-accent-500">
              <i className="ri-user-search-line text-xs"></i>
            </span>
            {specialtyTag}
          </div>

          <span className="w-px h-6 bg-background-200 hidden sm:block" />

          {/* Dropdown: Amputation Type Filter */}
          <div className="relative">
            <button
              type="button"
              onClick={() => toggleFilter("amputation")}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-[11px] md:text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${
                filterAmputation !== "all"
                  ? "bg-primary-50 text-primary-700 border border-primary-200"
                  : "bg-white text-foreground-500 border border-background-200 hover:border-background-300 hover:bg-background-50"
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-body-scan-line text-xs"></i>
              </span>
              {filterAmputation !== "all"
                ? amputationLabels[filterAmputation]
                : "Amputation Type"}
              <span className="w-4 h-4 flex items-center justify-center">
                <i
                  className={`ri-arrow-down-s-line text-[10px] transition-transform duration-200 ${filterOpen === "amputation" ? "rotate-180" : ""}`}
                ></i>
              </span>
            </button>

            {filterOpen === "amputation" && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl border border-background-200 shadow-lg z-30 overflow-hidden animate-fadeSlideIn">
                {Object.entries(amputationLabels).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setFilterAmputation(key as FilterKey);
                      setFilterOpen(null);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-[11px] md:text-xs transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                      filterAmputation === key
                        ? "bg-primary-50 text-primary-700 font-semibold"
                        : "text-foreground-500 hover:bg-background-50"
                    }`}
                  >
                    {filterAmputation === key && (
                      <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                        <i className="ri-check-line text-xs"></i>
                      </span>
                    )}
                    <span className={filterAmputation === key ? "" : "ml-6"}>
                      {label}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Dropdown: Distance Filter */}
          <div className="relative">
            <button
              type="button"
              onClick={() => toggleFilter("distance")}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-[11px] md:text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${
                filterDistance !== "all"
                  ? "bg-primary-50 text-primary-700 border border-primary-200"
                  : "bg-white text-foreground-500 border border-background-200 hover:border-background-300 hover:bg-background-50"
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-route-line text-xs"></i>
              </span>
              {filterDistance !== "all"
                ? distanceOptions.find((d) => d.value === filterDistance)?.label
                : "Distance"}
              <span className="w-4 h-4 flex items-center justify-center">
                <i
                  className={`ri-arrow-down-s-line text-[10px] transition-transform duration-200 ${filterOpen === "distance" ? "rotate-180" : ""}`}
                ></i>
              </span>
            </button>

            {filterOpen === "distance" && (
              <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl border border-background-200 shadow-lg z-30 overflow-hidden animate-fadeSlideIn">
                {distanceOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setFilterDistance(opt.value);
                      setFilterOpen(null);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-[11px] md:text-xs transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                      filterDistance === opt.value
                        ? "bg-primary-50 text-primary-700 font-semibold"
                        : "text-foreground-500 hover:bg-background-50"
                    }`}
                  >
                    {filterDistance === opt.value && (
                      <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                        <i className="ri-check-line text-xs"></i>
                      </span>
                    )}
                    <span
                      className={filterDistance === opt.value ? "" : "ml-6"}
                    >
                      {opt.label}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Dropdown: Rating Filter */}
          <div className="relative">
            <button
              type="button"
              onClick={() => toggleFilter("rating")}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-[11px] md:text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${
                filterRating !== "all"
                  ? "bg-primary-50 text-primary-700 border border-primary-200"
                  : "bg-white text-foreground-500 border border-background-200 hover:border-background-300 hover:bg-background-50"
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-star-line text-xs"></i>
              </span>
              {filterRating !== "all"
                ? ratingOptions.find((r) => r.value === filterRating)?.label
                : "Rating"}
              <span className="w-4 h-4 flex items-center justify-center">
                <i
                  className={`ri-arrow-down-s-line text-[10px] transition-transform duration-200 ${filterOpen === "rating" ? "rotate-180" : ""}`}
                ></i>
              </span>
            </button>

            {filterOpen === "rating" && (
              <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl border border-background-200 shadow-lg z-30 overflow-hidden animate-fadeSlideIn">
                {ratingOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setFilterRating(opt.value);
                      setFilterOpen(null);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-[11px] md:text-xs transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                      filterRating === opt.value
                        ? "bg-primary-50 text-primary-700 font-semibold"
                        : "text-foreground-500 hover:bg-background-50"
                    }`}
                  >
                    {filterRating === opt.value && (
                      <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                        <i className="ri-check-line text-xs"></i>
                      </span>
                    )}
                    <span className={filterRating === opt.value ? "" : "ml-6"}>
                      {opt.label}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Clear all filters */}
          {getActiveFilterCount() > 0 && (
            <button
              type="button"
              onClick={() => {
                setFilterAmputation("all");
                setFilterDistance("all");
                setFilterRating("all");
              }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] md:text-[11px] font-semibold text-foreground-400 hover:text-foreground-500 hover:bg-background-50 transition-all cursor-pointer whitespace-nowrap"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-close-line text-[10px]"></i>
              </span>
              Clear filters
            </button>
          )}

          {/* Results count */}
          <div className="ml-auto text-[10px] md:text-[11px] text-foreground-400">
            {filteredClinics.length} clinic
            {filteredClinics.length !== 1 ? "s" : ""} found
          </div>
        </div>

        {/* Empty State */}
        {filteredClinics.length === 0 && (
          <div className="relative z-10 text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <span className="w-8 h-8 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-xl"></i>
              </span>
            </div>
            <h3 className="font-heading text-lg font-bold text-foreground-800 mb-2">
              No clinics match your filters
            </h3>
            <p className="text-sm text-foreground-500 mb-4">
              Try expanding your distance range or adjusting other filters —
              there are great prosthetists in nearby areas too.
            </p>
            <button
              type="button"
              onClick={() => {
                setFilterAmputation("all");
                setFilterDistance("all");
                setFilterRating("all");
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-refresh-line text-xs"></i>
              </span>
              Reset all filters
            </button>
          </div>
        )}

        {/* Clinic Directory Cards — warmer redesign */}
        {filteredClinics.length > 0 && (
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredClinics.map((clinic) => {
              const isSelected = selectedClinic === clinic.id;
              const hasSpecialty = clinic.specialties.some((s) => {
                if (amputationType === "below-knee") return s === "Below Knee";
                if (amputationType === "above-knee") return s === "Above Knee";
                if (amputationType === "bilateral-bk")
                  return s === "Bilateral Below Knee";
                if (amputationType === "upper-extremity")
                  return s === "Upper Extremity";
                return true;
              });

              return (
                <div
                  key={clinic.id}
                  onClick={() => handleSelectClinic(clinic.id)}
                  className={`rounded-2xl border transition-all duration-300 cursor-pointer overflow-hidden ${
                    isSelected
                      ? "border-primary-400 bg-primary-50/30 shadow-[0_0_0_1px_rgba(0,0,0,0.03)]"
                      : "border-background-200 bg-white hover:border-background-300 hover:bg-background-50/80"
                  }`}
                >
                  {/* Card Top Gradient — subtle warmth */}
                  <div
                    className={`h-1.5 w-full ${isSelected ? "bg-primary-400" : "bg-accent-300/30"}`}
                  />

                  <div className="p-5 md:p-6">
                    {/* Clinic Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950 mb-1 leading-snug">
                          {clinic.name}
                        </h3>
                        <div className="flex items-center gap-2 text-[11px] md:text-xs text-foreground-500">
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-map-pin-line text-[10px]"></i>
                          </span>
                          <span>{clinic.distance} from you</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-accent-50">
                        <span className="w-3.5 h-3.5 flex items-center justify-center text-accent-500">
                          <i className="ri-star-fill text-xs"></i>
                        </span>
                        <span className="text-xs font-bold text-foreground-800">
                          {clinic.rating}
                        </span>
                      </div>
                    </div>

                    {/* Address */}
                    <p className="text-[11px] md:text-xs text-foreground-500 mb-4 leading-relaxed">
                      {clinic.address}
                    </p>

                    {/* Specialty Tags */}
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {clinic.specialties.map((spec) => {
                        const isMatch =
                          hasSpecialty &&
                          ((amputationType === "below-knee" &&
                            spec === "Below Knee") ||
                            (amputationType === "above-knee" &&
                              spec === "Above Knee") ||
                            (amputationType === "bilateral-bk" &&
                              spec === "Bilateral Below Knee") ||
                            (amputationType === "upper-extremity" &&
                              spec === "Upper Extremity"));
                        return (
                          <span
                            key={spec}
                            className={`inline-flex items-center px-2 py-0.5 rounded-md text-[9px] md:text-[10px] font-semibold ${
                              isMatch
                                ? "bg-accent-50 text-accent-700 border border-accent-200"
                                : "bg-background-100 text-foreground-500 border border-background-200"
                            }`}
                          >
                            {isMatch && (
                              <span className="w-2.5 h-2.5 flex items-center justify-center mr-1">
                                <i className="ri-check-line text-[8px]"></i>
                              </span>
                            )}
                            {spec}
                          </span>
                        );
                      })}
                      {clinic.acceptingPatients && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-green-50 text-green-700 border border-green-200 text-[9px] md:text-[10px] font-semibold">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          Accepting new patients
                        </span>
                      )}
                    </div>

                    {/* Prosthetists — The Heart of the Card */}
                    <div className="border-t border-background-100 pt-4 mb-4">
                      <p className="text-[10px] md:text-[11px] text-foreground-500 font-medium mb-3">
                        {clinic.prosthetists.length === 1
                          ? "The prosthetist who would work with you:"
                          : "The prosthetists who would work with you:"}
                      </p>
                      <div className="space-y-3">
                        {clinic.prosthetists.map((pro) => (
                          <div
                            key={pro.name}
                            className="flex items-center gap-3 group/pro"
                          >
                            <span className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-primary-100 ring-2 ring-background-50 group-hover/pro:ring-primary-200 transition-all">
                              <img
                                src={pro.photoUrl}
                                alt={pro.name}
                                className="w-full h-full object-cover object-top"
                              />
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs md:text-sm font-semibold text-foreground-800 truncate">
                                {pro.name}
                              </p>
                              <p className="text-[10px] md:text-[11px] text-foreground-400">
                                {pro.title} · {pro.yearsActive} years of
                                experience
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Reviews */}
                    <div className="flex items-center gap-2 mb-4 text-[10px] md:text-[11px] text-foreground-400">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-chat-3-line text-[10px]"></i>
                      </span>
                      <span>{clinic.reviewCount} patient reviews</span>
                    </div>

                    {/* Contact Button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRequestContact(clinic.id);
                      }}
                      className={`w-full py-3.5 rounded-xl text-xs md:text-sm font-bold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                        isSelected
                          ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                          : "bg-background-50 text-foreground-600 hover:bg-primary-50 hover:text-primary-700 border border-background-200"
                      }`}
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-message-3-line text-xs"></i>
                      </span>
                      I'd like this clinic to contact me
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom Summary */}
        {filteredClinics.length > 0 && (
          <div className="relative z-10 flex flex-wrap items-center justify-center gap-6 text-[11px] md:text-xs text-foreground-400 pt-2">
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shield-check-line text-xs"></i>
              </span>
              {clinics.reduce((sum, c) => sum + c.reviewCount, 0)} combined
              patient reviews across all clinics
            </span>
            <span className="hidden sm:inline">·</span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-heart-line text-xs"></i>
              </span>
              {clinics.reduce((sum, c) => sum + c.prosthetists.length, 0)}{" "}
              certified prosthetists ready to help
            </span>
          </div>
        )}

        {/* Subtle encouragement */}
        <div className="relative z-10 text-center pt-4 pb-2">
          <p className="text-[11px] md:text-xs text-foreground-400">
            Everything on this page is completely free for you.{" "}
            <Link
              to="/"
              className="underline underline-offset-4 hover:text-foreground-600 transition-colors cursor-pointer"
            >
              Learn how it works
            </Link>
          </p>
        </div>
      </main>

      {/* HIPAA Consent Modal — already warm, keeping as-is with minor tweaks */}
      {showConsent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={handleDecline}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-2xl max-w-[520px] w-full p-6 md:p-8 animate-fadeSlideIn">
            {/* Lock Icon */}
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-50 flex items-center justify-center">
              <span className="w-7 h-7 flex items-center justify-center text-accent-600">
                <i className="ri-lock-2-line text-xl"></i>
              </span>
            </div>

            <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 text-center mb-2">
              One last step — and you're done.
            </h3>
            <p className="text-sm text-foreground-500 text-center mb-3">
              We just need your permission to securely share your information
              with {agreedClinic}.
            </p>

            <p className="text-sm text-foreground-600 leading-relaxed text-center mb-3 max-w-md mx-auto">
              {hipaaConsentText.replace("{clinicName}", agreedClinic)}
            </p>

            <div className="rounded-lg border border-accent-200 bg-accent-50/60 p-4 mb-6">
              <div className="flex items-start gap-2.5">
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-accent-600 mt-0.5">
                  <i className="ri-information-line text-sm"></i>
                </span>
                <div className="text-[11px] md:text-xs text-accent-800 leading-relaxed">
                  <p className="font-semibold mb-1">What we'll share:</p>
                  <ul className="space-y-0.5">
                    <li className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-accent-400 flex-shrink-0" />
                      Your personal information and contact details
                    </li>
                    <li className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-accent-400 flex-shrink-0" />
                      Smartphone 3D scan metrics of your residual limb
                    </li>
                    <li className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-accent-400 flex-shrink-0" />
                      Preliminary clinical intake notes and K-Level assessment
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleDecline}
                className="flex-1 py-3.5 rounded-xl border-2 border-background-200 text-sm font-semibold text-foreground-500 hover:bg-background-50 hover:text-foreground-600 transition-all cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAgree}
                className="flex-1 py-3.5 rounded-xl bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line text-sm"></i>
                </span>
                I Agree
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Footer — warm */}
      <footer className="w-full bg-background-100 border-t border-background-200 mt-auto">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-start gap-2.5">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-400 mt-0.5">
              <i className="ri-information-line text-[11px]"></i>
            </span>
            <p className="text-[10px] md:text-[11px] text-foreground-500 leading-relaxed">
              {globalComplianceText}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
